/**
 * Convert reference ranges between units for common lab tests
 */

interface ConversionFactors {
  [testName: string]: {
    [fromTo: string]: number;
  };
}

const CONVERSION_FACTORS: ConversionFactors = {
  'hdl': {
    'mg/dl_to_mmol/l': 0.02586,
    'mmol/l_to_mg/dl': 38.67,
  },
  'ldl': {
    'mg/dl_to_mmol/l': 0.02586,
    'mmol/l_to_mg/dl': 38.67,
  },
  'cholesterol': {
    'mg/dl_to_mmol/l': 0.02586,
    'mmol/l_to_mg/dl': 38.67,
  },
  'triglycerides': {
    'mg/dl_to_mmol/l': 0.01129,
    'mmol/l_to_mg/dl': 88.57,
  },
  'glucose': {
    'mg/dl_to_mmol/l': 0.0555,
    'mmol/l_to_mg/dl': 18.02,
  }
};

export function convertReferenceRange(
  testName: string,
  refMin: number | null,
  refMax: number | null,
  fromUnit: string,
  toUnit: string
): { min: number | null; max: number | null } {
  if (refMin === null || refMax === null) {
    return { min: refMin, max: refMax };
  }

  // Normalize units
  const normalizedFrom = fromUnit.toLowerCase().replace(/[^a-z]/g, '');
  const normalizedTo = toUnit.toLowerCase().replace(/[^a-z]/g, '');

  // If units are the same, no conversion needed
  if (normalizedFrom === normalizedTo) {
    return { min: refMin, max: refMax };
  }

  // Look up conversion factor
  const testKey = testName.toLowerCase();
  const conversionKey = `${normalizedFrom}_to_${normalizedTo}`;
  
  const testFactors = CONVERSION_FACTORS[testKey];
  if (!testFactors || !testFactors[conversionKey]) {
    console.warn(`No conversion factor found for ${testName}: ${fromUnit} to ${toUnit}`);
    return { min: refMin, max: refMax };
  }

  const factor = testFactors[conversionKey];
  return {
    min: Math.round(refMin * factor * 10) / 10, // Round to 1 decimal place
    max: Math.round(refMax * factor * 10) / 10
  };
}

/**
 * Determine the likely original unit based on reference range values
 */
export function detectReferenceRangeUnit(
  testName: string,
  refMin: number,
  refMax: number
): string {
  const testLower = testName.toLowerCase();
  
  // HDL, LDL, Cholesterol
  if (testLower.includes('hdl') || testLower.includes('ldl') || testLower.includes('cholesterol')) {
    // If range is around 40-60, it's likely mg/dL
    if (refMin >= 20 && refMax <= 100) {
      return 'mg/dL';
    }
    // If range is around 1-2, it's likely mmol/L
    if (refMin >= 0.5 && refMax <= 5) {
      return 'mmol/L';
    }
  }
  
  // Glucose
  if (testLower.includes('glucose')) {
    // If range is around 70-100, it's likely mg/dL
    if (refMin >= 50 && refMax <= 150) {
      return 'mg/dL';
    }
    // If range is around 4-6, it's likely mmol/L
    if (refMin >= 2 && refMax <= 10) {
      return 'mmol/L';
    }
  }
  
  return '';
}